import React, { useState, useEffect } from 'react';
import { pluginService, PluginType, PluginConnection } from '../lib/pluginService';
import { PaletteRecord } from '../lib/database';

interface PluginManagerPanelProps {
  currentPalette?: PaletteRecord;
  onPaletteImported?: (palette: PaletteRecord) => void;
}

export const PluginManagerPanel: React.FC<PluginManagerPanelProps> = ({
  currentPalette,
  onPaletteImported,
}) => {
  const [connections, setConnections] = useState<PluginConnection[]>([]);

  const [isLoading, setIsLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState<{ text: string; type: 'success' | 'error' | 'info' } | null>(null);

  // Load connections on mount
  useEffect(() => {
    loadConnections();
    
    // Setup message handlers
    pluginService.onMessage('push_palette', () => {
      showStatus('Palette pushed successfully', 'success');
    });

    pluginService.onMessage('pull_palette', (message) => {
      const payload = message.payload as { palette?: PaletteRecord };
      if (payload?.palette && onPaletteImported) {
        onPaletteImported(payload.palette);
        showStatus('Palette imported successfully', 'success');
      }
    });

    pluginService.onMessage('error', (message) => {
      const payload = message.payload as { error?: string };
      showStatus(payload?.error || 'Plugin error occurred', 'error');
    });

    // Refresh connections periodically
    const interval = setInterval(loadConnections, 2000);
    
    return () => clearInterval(interval);
  }, [onPaletteImported]);

  const loadConnections = () => {
    const allConnections = pluginService.getAllConnections();
    setConnections(allConnections);
  };

  const showStatus = (text: string, type: 'success' | 'error' | 'info') => {
    setStatusMessage({ text, type });
    setTimeout(() => setStatusMessage(null), 3000);
  };

  const handleConnect = async (type: PluginType) => {
    setIsLoading(true);
    try {
      await pluginService.connect(type);
      showStatus(`Connecting to ${type}...`, 'info');
    } catch {
      showStatus(`Failed to connect to ${type}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisconnect = (type: PluginType) => {
    pluginService.disconnect(type);
    showStatus(`Disconnected from ${type}`, 'info');
  };

  const handlePushPalette = async (type: PluginType) => {
    if (!currentPalette) {
      showStatus('No palette selected', 'error');
      return;
    }

    setIsLoading(true);
    try {
      await pluginService.pushPalette(type, currentPalette);
      showStatus(`Pushing palette to ${type}...`, 'success');
    } catch (error) {
      showStatus(error instanceof Error ? error.message : 'Failed to push palette', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePullPalette = async (type: PluginType) => {
    setIsLoading(true);
    try {
      const palette = await pluginService.pullPalette(type);
      if (onPaletteImported) {
        onPaletteImported(palette);
      }
      showStatus('Palette pulled successfully', 'success');
    } catch (error) {
      showStatus(error instanceof Error ? error.message : 'Failed to pull palette', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePushTokens = async (type: PluginType) => {
    if (!currentPalette) {
      showStatus('No palette selected', 'error');
      return;
    }

    setIsLoading(true);
    try {
      await pluginService.pushTokens(type, currentPalette);
      showStatus(`Pushing tokens to ${type}...`, 'success');
    } catch (error) {
      showStatus(error instanceof Error ? error.message : 'Failed to push tokens', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'connected':
        return 'text-green-600 bg-green-50';
      case 'connecting':
        return 'text-yellow-600 bg-yellow-50';
      case 'error':
        return 'text-red-600 bg-red-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const getPluginIcon = (type: PluginType) => {
    switch (type) {
      case 'figma':
        return '🎨';
      case 'photoshop':
        return '🖼️';
      case 'illustrator':
        return '✏️';
      default:
        return '🔌';
    }
  };

  const getPluginName = (type: PluginType) => {
    switch (type) {
      case 'figma':
        return 'Figma';
      case 'photoshop':
        return 'Photoshop';
      case 'illustrator':
        return 'Illustrator';
      default:
        return type;
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Plugin Manager</h2>
        <p className="text-sm text-gray-600">
          Connect to design tools to sync palettes and tokens
        </p>
      </div>

      {/* Status Message */}
      {statusMessage && (
        <div
          className={`mb-4 p-3 rounded-lg text-sm ${
            statusMessage.type === 'success'
              ? 'bg-green-50 text-green-800 border border-green-200'
              : statusMessage.type === 'error'
              ? 'bg-red-50 text-red-800 border border-red-200'
              : 'bg-blue-50 text-blue-800 border border-blue-200'
          }`}
        >
          {statusMessage.text}
        </div>
      )}

      {/* Info Box */}
      <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-start">
          <span className="text-2xl mr-3">ℹ️</span>
          <div className="text-sm text-blue-900">
            <p className="font-semibold mb-1">Plugin Stub Implementation</p>
            <p className="text-blue-800">
              These are stub implementations that provide the infrastructure for plugin communication.
              To use these plugins, install them in your design tool and ensure both applications are running.
            </p>
          </div>
        </div>
      </div>

      {/* Plugin List */}
      <div className="space-y-4">
        {connections.map((connection) => (
          <div
            key={connection.type}
            className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-3">
                <span className="text-3xl">{getPluginIcon(connection.type)}</span>
                <div>
                  <h3 className="font-semibold text-gray-900">{getPluginName(connection.type)}</h3>
                  <div className="flex items-center space-x-2 mt-1">
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                        connection.status
                      )}`}
                    >
                      {connection.status}
                    </span>
                    {connection.lastConnected && (
                      <span className="text-xs text-gray-500">
                        Last: {new Date(connection.lastConnected).toLocaleTimeString()}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Connect/Disconnect Button */}
              {connection.status === 'connected' ? (
                <button
                  onClick={() => handleDisconnect(connection.type)}
                  className="px-3 py-1.5 text-sm font-medium text-red-700 bg-red-50 rounded-md hover:bg-red-100 transition-colors"
                  disabled={isLoading}
                >
                  Disconnect
                </button>
              ) : (
                <button
                  onClick={() => handleConnect(connection.type)}
                  className="px-3 py-1.5 text-sm font-medium text-blue-700 bg-blue-50 rounded-md hover:bg-blue-100 transition-colors disabled:opacity-50"
                  disabled={isLoading || connection.status === 'connecting'}
                >
                  {connection.status === 'connecting' ? 'Connecting...' : 'Connect'}
                </button>
              )}
            </div>

            {/* Error Message */}
            {connection.error && (
              <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-800">
                {connection.error}
              </div>
            )}

            {/* Action Buttons */}
            {connection.status === 'connected' && (
              <div className="grid grid-cols-3 gap-2">
                <button
                  onClick={() => handlePushPalette(connection.type)}
                  className="px-3 py-2 text-xs font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200 transition-colors disabled:opacity-50"
                  disabled={isLoading || !currentPalette}
                  title={!currentPalette ? 'No palette selected' : 'Push current palette'}
                >
                  Push Palette
                </button>
                <button
                  onClick={() => handlePullPalette(connection.type)}
                  className="px-3 py-2 text-xs font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200 transition-colors disabled:opacity-50"
                  disabled={isLoading}
                >
                  Pull Palette
                </button>
                <button
                  onClick={() => handlePushTokens(connection.type)}
                  className="px-3 py-2 text-xs font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200 transition-colors disabled:opacity-50"
                  disabled={isLoading || !currentPalette}
                  title={!currentPalette ? 'No palette selected' : 'Push as design tokens'}
                >
                  Push Tokens
                </button>
              </div>
            )}

            {/* Disconnected State Info */}
            {connection.status === 'disconnected' && (
              <div className="mt-3 p-2 bg-gray-50 rounded text-xs text-gray-600">
                <p>
                  <strong>Setup:</strong> Install the {getPluginName(connection.type)} plugin from the{' '}
                  <code className="bg-gray-200 px-1 rounded">plugins/{connection.type}</code> directory.
                </p>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Documentation Link */}
      <div className="mt-6 pt-6 border-t border-gray-200">
        <div className="text-sm text-gray-600">
          <p className="mb-2">
            <strong>Need help?</strong> See the plugin documentation for setup instructions:
          </p>
          <ul className="list-disc list-inside space-y-1 text-xs">
            <li>
              <code className="bg-gray-100 px-1 rounded">plugins/figma/README.md</code> - Figma plugin setup
            </li>
            <li>
              <code className="bg-gray-100 px-1 rounded">plugins/adobe-cep/README.md</code> - Adobe CEP plugin setup
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};
